/* eslint-disable react/jsx-props-no-spreading */

import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {AnalyticsTableMetricsEnum} from '../../../../../models/api/analyticsMetricsModel';
import {AnalyticsOptionsBucketBoundaries} from '../../../../../models/api/analyticsSavedViewModel';
import {fetchMetricWithBucketBoundariesAction} from '../../../../../store/panels/analytics/report/analyticsPanelReportActions';
import {HistoMetricRangeTypesEnum, HistoMetricSelection} from '../histoMetricSelectionDropdown';
import {NumberBucketsPicker} from './numberBucketsPicker';
import {TimeBucketsPicker} from './timeBucketsPicker';

/*
 * Props.
 */

interface HistoSettingsBucketsPickerProps extends PopoverTransferProps {
  metric: HistoMetricSelection;
  bucketBoundaries?: AnalyticsOptionsBucketBoundaries;
  color?: PaletteColorsEnum;
}

/*
 * Style.
 */

const DropdownContentWrapperDiv = styled.div`
  margin: 10px 0px;
`;

/*
 * Constants.
 */

const title = <Trans id="analytics-overview-buckets-title">Configure intervals</Trans>;

/*
 * Component.
 */

export const HistoSettingsBucketsPicker: FC<HistoSettingsBucketsPickerProps> = ({
  metric,
  bucketBoundaries,
  color = PaletteColorsEnum.BLUE,
  ...popoverTransferProps
}) => {
  const dispatch = useAppDispatch();

  const updateBoundariesAndFetchMetrics = (
    metricId: AnalyticsTableMetricsEnum,
    newBucketBoundaries: AnalyticsOptionsBucketBoundaries,
  ) => {
    dispatch(fetchMetricWithBucketBoundariesAction(metricId, newBucketBoundaries));

    popoverTransferProps.onRequestClose();
  };

  return (
    <Dropdown {...popoverTransferProps} width={326}>
      <DropdownHeader title={title} />
      <DropdownContentWrapperDiv>
        {metric.metricRangeType === HistoMetricRangeTypesEnum.TIME && (
          <TimeBucketsPicker
            metric={metric}
            bucketBoundaries={bucketBoundaries}
            color={color}
            onRequestClose={popoverTransferProps.onRequestClose}
            onApply={updateBoundariesAndFetchMetrics}
          />
        )}
        {metric.metricRangeType === HistoMetricRangeTypesEnum.NUMBER && (
          <NumberBucketsPicker
            metric={metric}
            bucketBoundaries={bucketBoundaries}
            color={color}
            onRequestClose={popoverTransferProps.onRequestClose}
            onApply={updateBoundariesAndFetchMetrics}
          />
        )}
      </DropdownContentWrapperDiv>
    </Dropdown>
  );
};
